// src/views/admin/ADConfigClasses.jsx
//
// PERMISSIONS (module "parametres") :
// - parametres.gerer_classes      : créer / modifier une classe + générer les classes standards
// - parametres.archiver_classes   : archiver / restaurer une classe
// - parametres.gerer_referentiels : états sanitaires, situations sociales, types d'élèves
//                                   (ajouter / modifier / activer-désactiver / supprimer)
// Niveaux et Séries sont gérés dans ADEtablissement (gerer_niveaux / gerer_series).
// Sans permission, les listes restent consultables en lecture seule.

import { useState, useEffect } from "react";
import { useAdminService } from "../../services/adminService";
import { Loader } from "../../components/Loader";
import { useToastHook } from "../../utils/useToast";
import { usePermissions } from "../../context/PermissionsContext";
import ADEtablissement from "./ADEtablissement";

/* ============================================================================
 * MODAL GÉNÉRIQUE (identique au pattern utilisé partout ailleurs)
 * ========================================================================== */
function Modal({ title, onClose, children, maxWidth = 480 }) {
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth }}>
        <div className="modal-title">
          {title}
          <button className="ibtn" style={{ border: "none" }} onClick={onClose}>
            <i className="ti ti-x" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

/* ============================================================================
 * PAGE PRINCIPALE — 6 onglets regroupant tout ce qui touche à la
 * "structure scolaire" et aux référentiels élèves. Avant, c'était éclaté
 * entre ADConfigClasses (niveaux/séries/classes), ADParametresEleves
 * (états sanitaires/situations sociales) et ADCaisseConfig (types d'élèves).
 * Maintenant : un seul point d'entrée, un seul jeu d'onglets.
 * ========================================================================== */
const ONGLETS = [
  { key: "niveaux", label: "Niveaux", icon: "ti-layers" },
  { key: "series", label: "Séries", icon: "ti-columns" },
  { key: "classes", label: "Classes", icon: "ti-school" },
  { key: "etats-sanitaires", label: "États sanitaires", icon: "ti-heartbeat" },
  { key: "situations-sociales", label: "Situations sociales", icon: "ti-users-group" },
  { key: "types-eleves", label: "Types d'élèves", icon: "ti-user-star" },
];

export default function ADConfigClasses({ slug }) {
  const [onglet, setOnglet] = useState("niveaux");

  return (
    <div>
      <div className="tab-nav">
        {ONGLETS.map(o => (
          <button
            key={o.key}
            className={`tab-btn ${onglet === o.key ? "active" : ""}`}
            onClick={() => setOnglet(o.key)}
          >
            <i className={`ti ${o.icon}`} /> <span>{o.label}</span>
          </button>
        ))}
      </div>

      {onglet === "niveaux" && <ADEtablissement slug={slug} section="niveaux" hideNav />}
      {onglet === "series" && <ADEtablissement slug={slug} section="filieres" hideNav />}
      {onglet === "classes" && <GestionClasses slug={slug} />}
      {onglet === "etats-sanitaires" && (
        <ReferentielCRUD slug={slug} config={CONFIG_ETATS_SANITAIRES} />
      )}
      {onglet === "situations-sociales" && (
        <ReferentielCRUD slug={slug} config={CONFIG_SITUATIONS_SOCIALES} />
      )}
      {onglet === "types-eleves" && <TypesEleves slug={slug} />}

      <style>{`
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
        .tab-nav {
          display: flex; gap: 4px; flex-wrap: wrap;
          background: var(--pr); border-radius: 10px; padding: 8px 12px;
          margin-bottom: 16px; box-shadow: 0 2px 8px rgba(0,0,0,0.1);
        }
        .tab-btn {
          display: flex; align-items: center; gap: 6px; padding: 10px 18px;
          border: none; border-radius: 6px; font-size: 13px; font-weight: 600;
          cursor: pointer; transition: all 0.3s ease; background: transparent; color: #fff;
        }
        .tab-btn:hover { background: rgba(255,255,255,0.15); }
        .tab-btn.active { background: white; color: var(--pr); box-shadow: 0 2px 8px rgba(0,0,0,0.15); }
        .act button {
          width: 30px; height: 30px; border-radius: 4px; border: none; cursor: pointer;
          color: var(--m); display: flex; align-items: center; justify-content: center;
          font-size: 14px; transition: all .2s; background: var(--s2);
        }
        .act button:hover { opacity: 0.8; }
        .act button.del { background: var(--rd); color: white; }
        @media (max-width: 576px) {
          .tab-btn span { display: none; }
        }
      `}</style>
    </div>
  );
}

/* ============================================================================
 * GESTION DES CLASSES
 * - parametres.gerer_classes    : créer / modifier / générer les classes standards
 * - parametres.archiver_classes : archiver / restaurer
 * ========================================================================== */
const EMPTY_CLASSE = { nom: "", niveau: "", filiere: "", annee_scolaire: "" };

function GestionClasses({ slug }) {
  const { canSee } = usePermissions();
  const peutGererClasses = canSee("parametres.gerer_classes");
  const peutArchiver = canSee("parametres.archiver_classes");
  const afficherActions = peutGererClasses || peutArchiver;

  const [classes, setClasses] = useState([]);
  const [annees, setAnnees] = useState([]);
  const [niveaux, setNiveaux] = useState([]);
  const [filieres, setFilieres] = useState([]);
  const [enseignants, setEnseignants] = useState([]);
  const [matieres, setMatieres] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [search, setSearch] = useState("");
  const [anneeFilt, setAnneeFilt] = useState("");
  const [showArchived, setShowArchived] = useState(false);

  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY_CLASSE);
  const [pickedIds, setPickedIds] = useState([]);
  const [parents, setParents] = useState(null);

  const toast = useToastHook();
  const {
    getClasses, createClasse, updateClasse, archiveClasse, restoreClasse,
    assignEnseignantsClasse, assignMatieresClasse,
    getAnneesScolaires, getUtilisateurs,
    getNiveaux, getFilieres, getMatieres, getParentsParClasse,
    genererClassesStandard,
  } = useAdminService();

  useEffect(() => {
    if (slug) init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  useEffect(() => {
    if (slug) { loadClasses(); loadMatieres(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, anneeFilt, showArchived]);

  const init = async () => {
    setLoading(true);
    try {
      const [anneesData, niveauxData, filieresData, ensData] = await Promise.all([
        getAnneesScolaires(slug, { show_archived: "false" }),
        getNiveaux(slug),
        getFilieres(slug),
        getUtilisateurs(slug, { role: "ENSEIGNANT", page_size: "all" }),
      ]);
      setAnnees(anneesData);
      setNiveaux(niveauxData);
      setFilieres(filieresData);
      setEnseignants(ensData.results || ensData);

      const anneeActive = anneesData.find(a => a.actif);
      setAnneeFilt(String((anneeActive || anneesData[0])?.id || ""));
    } catch (error) {
      toast.error("Impossible de charger les données de référence");
    } finally {
      setLoading(false);
    }
  };

  const loadClasses = async () => {
    try {
      const params = { show_archived: showArchived ? "true" : "false" };
      if (anneeFilt) params.annee_scolaire = anneeFilt;
      const data = await getClasses(slug, params);
      setClasses(data);
    } catch (error) {
      toast.error("Impossible de charger les classes");
    }
  };

  const loadMatieres = async () => {
    if (!anneeFilt) return;
    try {
      const data = await getMatieres(slug, { annee_scolaire: anneeFilt });
      setMatieres(data);
    } catch (error) {
      toast.error("Impossible de charger les matières");
    }
  };

  const filtered = classes.filter(c => c.nom?.toLowerCase().includes(search.toLowerCase()));
  const anneeActive = annees.find(a => a.actif);

  const openCreate = () => {
    if (!peutGererClasses) return;
    if (!anneeActive) {
      toast.error("Aucune année scolaire active. Activez une année scolaire dans l'onglet « Années scolaires ».");
      return;
    }
    setForm({ nom: "", niveau: "", filiere: "", annee_scolaire: anneeFilt || "" });
    setModal("create");
  };

  const openEdit = (c) => {
    if (!peutGererClasses) return;
    setSelected(c);
    setForm({ nom: c.nom, niveau: c.niveau, filiere: c.filiere || "", annee_scolaire: c.annee_scolaire });
    setModal("edit");
  };

  const openDetails = (c) => { setSelected(c); setModal("details"); };
  const handleFormChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    if (!peutGererClasses) return;
    if (!form.nom.trim()) { toast.error("Le nom est requis"); return; }
    if (!form.niveau) { toast.error("Le niveau est requis"); return; }
    if (!form.annee_scolaire) { toast.error("L'année scolaire est requise"); return; }

    setSaving(true);
    try {
      const dataToSend = {
        nom: form.nom.trim(),
        niveau: form.niveau,
        filiere: form.filiere || null,
        annee_scolaire: form.annee_scolaire,
      };
      if (modal === "create") {
        const newClasse = await createClasse(slug, dataToSend);
        setClasses(prev => [...prev, newClasse]);
        toast.success(`Classe "${newClasse.nom}" créée avec succès`);
      } else {
        const updated = await updateClasse(slug, selected.id, dataToSend);
        setClasses(prev => prev.map(c => c.id === selected.id ? updated : c));
        toast.success(`Classe "${updated.nom}" modifiée avec succès`);
      }
      setModal(null);
    } catch (error) {
      const data = error.response?.data;
      const msg = data?.nom?.[0] || data?.annee_scolaire || data?.filiere || "Une erreur est survenue";
      toast.error(Array.isArray(msg) ? msg[0] : msg);
    } finally {
      setSaving(false);
    }
  };

  const openArchive = (c) => {
    if (!peutArchiver) return;
    setSelected(c);
    setModal("archive");
  };

  const handleArchive = async () => {
    if (!peutArchiver) return;
    setSaving(true);
    try {
      await archiveClasse(slug, selected.id);
      setClasses(prev => prev.map(c => c.id === selected.id ? { ...c, archived: true } : c));
      toast.success(`Classe "${selected.nom}" archivée avec succès`);
      setModal(null);
    } catch (error) {
      toast.error(error.response?.data?.error || "Impossible d'archiver cette classe");
    } finally {
      setSaving(false);
    }
  };

  const handleRestore = async (c) => {
    if (!peutArchiver) return;
    try {
      await restoreClasse(slug, c.id);
      setClasses(prev => prev.map(x => x.id === c.id ? { ...x, archived: false } : x));
      toast.success(`Classe "${c.nom}" restaurée avec succès`);
    } catch (error) {
      toast.error(error.response?.data?.error || "Impossible de restaurer cette classe");
    }
  };

  const openAssignEns = (c) => {
    setSelected(c);
    setPickedIds((c.enseignants_detail || []).map(e => e.id));
    setModal("assign-ens");
  };
  const openAssignMat = (c) => {
    setSelected(c);
    setPickedIds((c.matieres_detail || []).map(m => m.id));
    setModal("assign-mat");
  };

  const togglePicked = (id) => {
    setPickedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleAssign = async () => {
    setSaving(true);
    try {
      if (modal === "assign-ens") {
        const updated = await assignEnseignantsClasse(slug, selected.id, { enseignants: pickedIds, mode: "replace" });
        setClasses(prev => prev.map(c => c.id === selected.id ? updated : c));
        toast.success("Enseignants mis à jour avec succès");
      } else {
        const updated = await assignMatieresClasse(slug, selected.id, { matieres: pickedIds, mode: "replace" });
        setClasses(prev => prev.map(c => c.id === selected.id ? updated : c));
        toast.success("Matières mises à jour avec succès");
      }
      setModal(null);
    } catch (error) {
      toast.error("Une erreur est survenue");
    } finally {
      setSaving(false);
    }
  };

  const openParents = async (c) => {
    setSelected(c);
    setModal("parents");
    setParents(null);
    try {
      const data = await getParentsParClasse(slug, c.id);
      setParents(data);
    } catch (error) {
      toast.error("Impossible de charger les parents");
      setParents([]);
    }
  };

  const handleGenererClassesStandard = async () => {
    if (!peutGererClasses || !anneeFilt) return;
    setGenerating(true);
    try {
      const res = await genererClassesStandard(slug, anneeFilt);
      if (res.classes_creees > 0) {
        toast.success(`${res.classes_creees} classe(s) générée(s) avec succès`);
        loadClasses();
      } else {
        toast.info("Aucune classe créée (déjà générées ?)");
      }
    } catch (error) {
      toast.error(error.response?.data?.error || "Impossible de générer les classes standards");
    } finally {
      setGenerating(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div>
      {!anneeActive && (
        <div className="alert-banner">
          <i className="ti ti-alert-triangle" />
          <span>Aucune année scolaire active. Activez-en une dans l'onglet « Années scolaires » avant de créer des classes.</span>
        </div>
      )}

      <div className="sb2">
        <input type="text" placeholder="Rechercher une classe…" value={search} onChange={e => setSearch(e.target.value)} />
        <select value={anneeFilt} onChange={e => setAnneeFilt(e.target.value)}>
          {annees.map(a => <option key={a.id} value={a.id}>{a.nom}{a.actif ? " (active)" : ""}</option>)}
        </select>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
          <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} />
          Voir archivées
        </label>
        <button className="bsec" onClick={loadClasses}><i className="ti ti-refresh" />Actualiser</button>
        {/* parametres.gerer_classes */}
        {peutGererClasses && anneeFilt && filtered.length === 0 && !showArchived && (
          <button className="bsec" onClick={handleGenererClassesStandard} disabled={generating}>
            {generating ? (
              <><i className="ti ti-loader" style={{ animation: "spin 1s linear infinite" }} /> Génération...</>
            ) : (
              <><i className="ti ti-list-numbers" />Générer les classes standards</>
            )}
          </button>
        )}
        {peutGererClasses && (
          <button className="bprim" onClick={openCreate} disabled={!anneeActive}>
            <i className="ti ti-plus" />Nouvelle classe
          </button>
        )}
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>Classe</th>
              <th>Niveau</th>
              <th>Séries</th>
              <th>Enseignants</th>
              <th>Matières</th>
              <th>Statut</th>
              {afficherActions && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {filtered.map(c => (
              <tr key={c.id}>
                <td style={{ fontWeight: 500 }}>
                  {c.nom}
                  {c.archived && <span className="pill warn" style={{ marginLeft: 8 }}>Archivée</span>}
                </td>
                <td>{c.niveau_nom || "—"}</td>
                <td>{c.filiere_nom || "—"}</td>
                <td>{c.enseignants_detail?.length || 0}</td>
                <td>{c.matieres_detail?.length || 0}</td>
                <td><span className={`pill ${c.archived ? "warn" : "ok"}`}>{c.archived ? "Archivée" : "Active"}</span></td>
                {afficherActions && (
                  <td>
                    <div className="act">
                      {/* parametres.gerer_classes */}
                      {peutGererClasses && !c.archived && (
                        <button title="Modifier" onClick={() => openEdit(c)}
                          style={{ background: "#ffc107", color: "black" }}
                          onMouseEnter={e => { e.currentTarget.style.opacity = "0.8"; }}
                          onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}>
                          <i className="ti ti-edit" />
                        </button>
                      )}
                      {/* parametres.archiver_classes (archiver + restaurer) */}
                      {peutArchiver && (!c.archived ? (
                        <button title="Archiver" onClick={() => openArchive(c)}
                          style={{ background: "var(--pr)", color: "white" }}
                          onMouseEnter={e => { e.currentTarget.style.opacity = "0.8"; }}
                          onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}>
                          <i className="ti ti-archive" />
                        </button>
                      ) : (
                        <button title="Restaurer" onClick={() => handleRestore(c)}
                          style={{ background: "#20c997", color: "white" }}
                          onMouseEnter={e => { e.currentTarget.style.opacity = "0.8"; }}
                          onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}>
                          <i className="ti ti-restore" />
                        </button>
                      ))}
                    </div>
                  </td>
                )}
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={afficherActions ? 7 : 6} style={{ textAlign: "center", color: "var(--h)", padding: "24px" }}>Aucune classe trouvée</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {peutGererClasses && (modal === "create" || modal === "edit") && (
        <Modal title={modal === "create" ? "Nouvelle classe" : "Modifier la classe"} onClose={() => setModal(null)}>
          <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
            <div className="fg">
              <label>Nom *</label>
              <input type="text" placeholder="Ex: 3e A" value={form.nom} onChange={e => handleFormChange('nom', e.target.value)} />
            </div>
            <div className="fg">
              <label>Niveau *</label>
              <select value={form.niveau} onChange={e => handleFormChange('niveau', e.target.value)}>
                <option value="">-- Sélectionner --</option>
                {niveaux.map(n => <option key={n.id} value={n.id}>{n.nom}</option>)}
              </select>
            </div>
            <div className="fg">
              <label>Séries (lycée uniquement)</label>
              <select value={form.filiere} onChange={e => handleFormChange('filiere', e.target.value)}>
                <option value="">-- Aucune --</option>
                {filieres.map(f => <option key={f.id} value={f.id}>{f.nom}</option>)}
              </select>
            </div>
            <div className="fg">
              <label>Année scolaire *</label>
              <select value={form.annee_scolaire} onChange={e => handleFormChange('annee_scolaire', e.target.value)}>
                <option value="">-- Sélectionner --</option>
                {annees.map(a => <option key={a.id} value={a.id}>{a.nom}</option>)}
              </select>
            </div>
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
            <button className="bprim" onClick={handleSave} disabled={saving}>
              {saving ? "En cours..." : <><i className="ti ti-check" /> {modal === "create" ? "Créer" : "Enregistrer"}</>}
            </button>
          </div>
        </Modal>
      )}

      {modal === "details" && selected && (
        <Modal title={`Détails de la classe ${selected.nom}`} onClose={() => setModal(null)}>
          <div style={{ marginBottom: 20 }}>
            <h4 style={{ fontSize: 15, fontWeight: 600, color: "var(--t)", marginBottom: 10 }}>
              <i className="ti ti-users" style={{ marginRight: 6 }} /> Enseignants assignés
            </h4>
            {selected.enseignants_detail?.length > 0 ? (
              selected.enseignants_detail.map(e => (
                <div key={e.id} className="avc" style={{ padding: 6, borderBottom: "0.5px solid var(--b)" }}>
                  <div className="av pu" style={{ width: 20, height: 20, fontSize: 10 }}>E</div>
                  <span>{e.username}{e.nom ? ` (${e.prenom} ${e.nom})` : ""}</span>
                </div>
              ))
            ) : (
              <div style={{ color: "var(--h)", fontSize: 13 }}>Aucun enseignant assigné</div>
            )}
          </div>
          <div>
            <h4 style={{ fontSize: 15, fontWeight: 600, color: "var(--t)", marginBottom: 10 }}>
              <i className="ti ti-book" style={{ marginRight: 6 }} /> Matières assignées
            </h4>
            {selected.matieres_detail?.length > 0 ? (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {selected.matieres_detail.map(m => <span key={m.id} className="pill info">{m.nom}</span>)}
              </div>
            ) : (
              <div style={{ color: "var(--h)", fontSize: 13 }}>Aucune matière assignée</div>
            )}
          </div>
        </Modal>
      )}

      {peutArchiver && modal === "archive" && selected && (
        <Modal title="Archiver la classe" onClose={() => setModal(null)}>
          <div style={{ fontSize: 14, color: "var(--m)" }}>Archiver <strong>{selected.nom}</strong> ?</div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
            <button className="bprim" onClick={handleArchive} disabled={saving}>{saving ? "..." : "Archiver"}</button>
          </div>
        </Modal>
      )}

      {(modal === "assign-ens" || modal === "assign-mat") && selected && (
        <Modal title={modal === "assign-ens" ? `Enseignants — ${selected.nom}` : `Matières — ${selected.nom}`} onClose={() => setModal(null)}>
          <div style={{ maxHeight: 300, overflowY: "auto", display: "flex", flexDirection: "column", gap: 6 }}>
            {(modal === "assign-ens" ? enseignants : matieres).map(item => (
              <label key={item.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
                <input type="checkbox" checked={pickedIds.includes(item.id)} onChange={() => togglePicked(item.id)} />
                {modal === "assign-ens" ? `${item.first_name} ${item.last_name}` : item.nom}
              </label>
            ))}
            {(modal === "assign-ens" ? enseignants : matieres).length === 0 && (
              <div style={{ color: "var(--h)", fontSize: 13 }}>
                {modal === "assign-ens" ? "Aucun enseignant trouvé." : "Aucune matière pour cette année scolaire."}
              </div>
            )}
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
            <button className="bprim" onClick={handleAssign} disabled={saving}>{saving ? "..." : "Enregistrer"}</button>
          </div>
        </Modal>
      )}

      {modal === "parents" && selected && (
        <Modal title={`Parents — ${selected.nom}`} onClose={() => setModal(null)}>
          {!parents ? <Loader /> : parents.length === 0 ? (
            <div style={{ color: "var(--h)" }}>Aucun parent trouvé pour cette classe.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {parents.map(p => (
                <div key={`${p.parent_id}-${p.enfant.id}`} className="card" style={{ padding: 10 }}>
                  <div style={{ fontWeight: 500 }}>{p.prenom_parent} {p.nom_parent}</div>
                  <div style={{ fontSize: 12, color: "var(--h)" }}>Enfant : {p.enfant.prenom} {p.enfant.nom}</div>
                </div>
              ))}
            </div>
          )}
        </Modal>
      )}
    </div>
  );
}

/* ============================================================================
 * COMPOSANT GÉNÉRIQUE RÉUTILISABLE — États sanitaires / Situations sociales
 * Permission : parametres.gerer_referentiels (sans elle : lecture seule).
 * ========================================================================== */
function ReferentielCRUD({ slug, config }) {
  const { canSee } = usePermissions();
  const peutGerer = canSee("parametres.gerer_referentiels");

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showInactifs, setShowInactifs] = useState(false);
  const [newLibelle, setNewLibelle] = useState("");
  const [modal, setModal] = useState(null); // "edit" | "delete"
  const [selected, setSelected] = useState(null);
  const [editLibelle, setEditLibelle] = useState("");

  const service = useAdminService();
  const toast = useToastHook();
  const { getAll, create, update, remove, placeholder, label } = config(service);

  useEffect(() => {
    if (slug) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, showInactifs]);

  const load = async () => {
    setLoading(true);
    try {
      setItems(await getAll(slug, { actifs_seulement: showInactifs ? "false" : "true" }));
    } catch {
      toast.error("Impossible de charger la liste");
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!peutGerer) return;
    const libelle = newLibelle.trim();
    if (!libelle) { toast.error("Le libellé est requis"); return; }
    setSaving(true);
    try {
      await create(slug, { libelle });
      toast.success(`"${libelle}" ajouté avec succès`);
      setNewLibelle("");
      await load();
    } catch (error) {
      const data = error.response?.data;
      const msg = data?.libelle?.[0] || data?.non_field_errors?.[0] || data?.error || "Erreur lors de l'ajout";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDownCreate = (e) => { if (e.key === "Enter") handleCreate(); };

  const openEdit = (item) => {
    if (!peutGerer) return;
    setSelected(item);
    setEditLibelle(item.libelle);
    setModal("edit");
  };

  const handleSaveEdit = async () => {
    if (!peutGerer) return;
    const libelle = editLibelle.trim();
    if (!libelle) { toast.error("Le libellé est requis"); return; }
    setSaving(true);
    try {
      await update(slug, selected.id, { libelle });
      toast.success("Modifié avec succès");
      setModal(null);
      await load();
    } catch (error) {
      const data = error.response?.data;
      const msg = data?.libelle?.[0] || data?.non_field_errors?.[0] || data?.error || "Erreur lors de la modification";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActif = async (item) => {
    if (!peutGerer) return;
    try {
      await update(slug, item.id, { actif: !item.actif });
      toast.success(item.actif ? "Désactivé" : "Réactivé");
      await load();
    } catch {
      toast.error("Impossible de modifier le statut");
    }
  };

  const openDelete = (item) => {
    if (!peutGerer) return;
    setSelected(item);
    setModal("delete");
  };

  const handleDelete = async () => {
    if (!peutGerer) return;
    setSaving(true);
    try {
      const response = await remove(slug, selected.id);
      if (response?.message) {
        toast.warning(response.message);
      } else {
        toast.success("Supprimé avec succès");
      }
      setModal(null);
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.response?.data?.error || "Impossible de supprimer");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div>
      <div className="sb2">
        {peutGerer && (
          <>
            <input
              type="text"
              placeholder={placeholder}
              value={newLibelle}
              onChange={e => setNewLibelle(e.target.value)}
              onKeyDown={handleKeyDownCreate}
              style={{ minWidth: 320, flex: 1 }}
            />
            <button className="bprim" onClick={handleCreate} disabled={saving}>
              <i className="ti ti-plus" /> Ajouter
            </button>
          </>
        )}

        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: "var(--m)", marginLeft: "auto" }}>
          <input type="checkbox" checked={showInactifs} onChange={e => setShowInactifs(e.target.checked)} />
          Afficher les valeurs désactivées
        </label>
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ width: peutGerer ? "60%" : "80%" }}>Libellé</th>
              <th style={{ width: "20%" }}>Statut</th>
              {peutGerer && <th style={{ width: "20%" }}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {items.length > 0 ? (
              items.map(item => (
                <tr key={item.id}>
                  <td style={{ fontWeight: 500 }}>{item.libelle}</td>
                  <td>
                    <span className={`pill ${item.actif ? "info" : "pu"}`}>
                      {item.actif ? "Actif" : "Désactivé"}
                    </span>
                  </td>
                  {peutGerer && (
                    <td>
                      <div className="act">
                        <button
                          title={item.actif ? "Désactiver" : "Réactiver"}
                          onClick={() => handleToggleActif(item)}
                          style={{ background: item.actif ? "#6c757d" : "#28a745", color: "white" }}
                        >
                          <i className={`ti ${item.actif ? "ti-eye-off" : "ti-eye"}`} />
                        </button>
                        <button title="Modifier" onClick={() => openEdit(item)} style={{ background: "#ffc107", color: "black" }}>
                          <i className="ti ti-edit" />
                        </button>
                        <button className="del" title="Supprimer" onClick={() => openDelete(item)}>
                          <i className="ti ti-trash" />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={peutGerer ? 3 : 2} style={{ textAlign: "center", color: "var(--h)", padding: 40 }}>
                  Aucune valeur configurée pour {label.toLowerCase()}.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {peutGerer && modal === "edit" && selected && (
        <Modal title={`Modifier — ${label}`} onClose={() => setModal(null)}>
          <div className="fg">
            <label>Libellé <span style={{ color: "var(--rd)" }}>*</span></label>
            <input type="text" value={editLibelle} onChange={e => setEditLibelle(e.target.value)} autoFocus />
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
            <button className="bprim" onClick={handleSaveEdit} disabled={saving}>
              {saving
                ? <><i className="ti ti-loader" style={{ animation: "spin 1s linear infinite" }} /> En cours...</>
                : <><i className="ti ti-check" /> Enregistrer</>}
            </button>
          </div>
        </Modal>
      )}

      {peutGerer && modal === "delete" && selected && (
        <Modal title={`Supprimer — ${label}`} onClose={() => setModal(null)}>
          <div style={{ fontSize: 14, color: "var(--m)", lineHeight: 1.6 }}>
            Supprimer <strong style={{ color: "var(--t)" }}>{selected.libelle}</strong> ?
            <div style={{ color: "var(--h)", marginTop: 8, fontSize: 13 }}>
              <i className="ti ti-info-circle" /> Si cette valeur est déjà utilisée par des élèves, elle sera
              désactivée plutôt que supprimée, pour ne pas casser leur historique.
            </div>
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
            <button className="bprim rd" onClick={handleDelete} disabled={saving}>
              {saving
                ? <><i className="ti ti-loader" style={{ animation: "spin 1s linear infinite" }} /> Suppression...</>
                : <><i className="ti ti-trash" /> Supprimer</>}
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

// Configs passées à ReferentielCRUD (une fonction, pour récupérer les
// bonnes méthodes depuis useAdminService() au moment du rendu).
const CONFIG_ETATS_SANITAIRES = (service) => ({
  getAll: service.getEtatsSanitaires,
  create: service.createEtatSanitaire,
  update: service.updateEtatSanitaire,
  remove: service.deleteEtatSanitaire,
  placeholder: "Ex: Bien portant, Handicap moteur, Asthmatique...",
  label: "États sanitaires",
});

const CONFIG_SITUATIONS_SOCIALES = (service) => ({
  getAll: service.getSituationsSociales,
  create: service.createSituationSociale,
  update: service.updateSituationSociale,
  remove: service.deleteSituationSociale,
  placeholder: "Ex: Rien à signaler, Orphelin de père, Orphelin de mère...",
  label: "Situations sociales",
});

/* ============================================================================
 * TYPES D'ÉLÈVES — permission : parametres.gerer_referentiels
 * (le module caisse continue d'utiliser les mêmes endpoints ; on déplace
 * juste l'écran de gestion ici, dans la page structure scolaire).
 * ========================================================================== */
const EMPTY_TE = { code: "", libelle: "", ordre: 0, actif: true };

function TypesEleves({ slug }) {
  const { canSee } = usePermissions();
  const peutGerer = canSee("parametres.gerer_referentiels");

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY_TE);

  const toast = useToastHook();
  const { getTypesEleves, createTypeEleve, updateTypeEleve, deleteTypeEleve } = useAdminService();

  useEffect(() => { if (slug) load(); }, [slug]);

  const load = async () => {
    setLoading(true);
    try {
      setItems(await getTypesEleves(slug));
    } catch (error) {
      toast.error("Impossible de charger les types d'élèves");
    } finally {
      setLoading(false);
    }
  };

  const filtered = items.filter(t => t.libelle?.toLowerCase().includes(search.toLowerCase()) || t.code?.toLowerCase().includes(search.toLowerCase()));

  const openCreate = () => { if (!peutGerer) return; setForm(EMPTY_TE); setModal("create"); };
  const openEdit = (t) => {
    if (!peutGerer) return;
    setSelected(t);
    setForm({ code: t.code, libelle: t.libelle, ordre: t.ordre, actif: t.actif });
    setModal("edit");
  };
  const openDelete = (t) => { if (!peutGerer) return; setSelected(t); setModal("delete"); };
  const change = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    if (!peutGerer) return;
    if (!form.code.trim() || !form.libelle.trim()) { toast.error("Le code et le libellé sont requis"); return; }
    setSaving(true);
    try {
      if (modal === "create") {
        const created = await createTypeEleve(slug, form);
        setItems(prev => [...prev, created]);
        toast.success(`Type d'élève "${created.libelle}" créé avec succès`);
      } else {
        const updated = await updateTypeEleve(slug, selected.id, form);
        setItems(prev => prev.map(t => t.id === selected.id ? updated : t));
        toast.success(`Type d'élève "${updated.libelle}" modifié avec succès`);
      }
      setModal(null);
    } catch (error) {
      const data = error.response?.data;
      const msg = data?.code?.[0] || data?.libelle?.[0] || data?.non_field_errors?.[0] || "Une erreur est survenue";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!peutGerer) return;
    setSaving(true);
    try {
      await deleteTypeEleve(slug, selected.id);
      setItems(prev => prev.filter(t => t.id !== selected.id));
      toast.success(`Type d'élève "${selected.libelle}" supprimé avec succès`);
      setModal(null);
    } catch (error) {
      toast.error(error.response?.data?.error || "Impossible de supprimer ce type d'élève");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader />;

  return (
    <div>
      <div className="sb2">
        <input type="text" placeholder="Rechercher un type d'élève…" value={search} onChange={e => setSearch(e.target.value)} />
        <button className="bsec" onClick={load}><i className="ti ti-refresh" />Actualiser</button>
        {peutGerer && (
          <button className="bprim" onClick={openCreate}><i className="ti ti-plus" />Nouveau type d'élève</button>
        )}
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>Ordre</th><th>Code</th><th>Libellé</th><th>Statut</th>
              {peutGerer && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {[...filtered].sort((a, b) => a.ordre - b.ordre).map(t => (
              <tr key={t.id}>
                <td>{t.ordre}</td>
                <td style={{ fontWeight: 500 }}>{t.code}</td>
                <td>{t.libelle}</td>
                <td><span className={`pill ${t.actif ? "ok" : "warn"}`}>{t.actif ? "Actif" : "Inactif"}</span></td>
                {peutGerer && (
                  <td>
                    <div className="act">
                      <button title="Modifier" onClick={() => openEdit(t)}><i className="ti ti-edit" /></button>
                      <button title="Supprimer" className="del" onClick={() => openDelete(t)}><i className="ti ti-trash" /></button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={peutGerer ? 5 : 4} style={{ textAlign: "center", color: "var(--h)", padding: "24px" }}>Aucun type d'élève trouvé</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {peutGerer && (modal === "create" || modal === "edit") && (
        <Modal title={modal === "create" ? "Nouveau type d'élève" : "Modifier le type d'élève"} onClose={() => setModal(null)}>
          <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
            <div className="fgrid">
              <div className="fg">
                <label>Code *</label>
                <input type="text" placeholder="Ex: ESH" value={form.code} onChange={e => change('code', e.target.value.toUpperCase())} />
              </div>
              <div className="fg">
                <label>Ordre d'affichage</label>
                <input type="number" value={form.ordre} onChange={e => change('ordre', Number(e.target.value))} />
              </div>
            </div>
            <div className="fg">
              <label>Libellé *</label>
              <input type="text" placeholder="Ex: Élève en situation de handicap" value={form.libelle} onChange={e => change('libelle', e.target.value)} />
            </div>
            <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 14 }}>
              <input type="checkbox" checked={form.actif} onChange={e => change('actif', e.target.checked)} />
              Actif
            </label>
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
            <button className="bprim" onClick={handleSave} disabled={saving}>
              {saving ? "En cours..." : <><i className="ti ti-check" /> {modal === "create" ? "Créer" : "Enregistrer"}</>}
            </button>
          </div>
        </Modal>
      )}

      {peutGerer && modal === "delete" && selected && (
        <Modal title="Supprimer le type d'élève" onClose={() => setModal(null)}>
          <div style={{ fontSize: 14, color: "var(--m)" }}>Supprimer <strong>{selected.libelle}</strong> ?</div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)} disabled={saving}>Annuler</button>
            <button className="bprim rd" onClick={handleDelete} disabled={saving}>{saving ? "..." : "Supprimer"}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}